import React, { useEffect, lazy, Suspense, useState, startTransition } from 'react';
import { ThemeMascot } from './components/ui/theme-mascots';
import { Switch, Route, Router as WouterRouter } from 'wouter';
import { queryClient } from './lib/queryClient';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/toaster';
import { TooltipProvider } from './components/ui/tooltip';
import { ThemeProvider } from './components/theme-provider';
import { UserProvider } from './contexts/user-context';
import { AuthProvider as FirebaseAuthProvider } from './contexts/firebase-auth';
import { AuthProvider, useAuth } from './contexts/integrated-auth';
import { RealTimeUpdatesProvider } from './contexts/real-time-updates-context';
import { ErrorProvider } from './contexts/error-context';
import { ProtectedRoute } from './lib/protected-route';
import { TourProvider } from './tour/tour-context';
import ErrorBoundary from './components/ErrorBoundary';
import { setupGlobalErrorCatching } from './lib/errorReporting';
import { captureReferrer } from './lib/invite-storage';
import { setupDebugCommands, monitorNetworkRequests } from './lib/debug-utils';
import { debugApiClient } from './lib/debug-api-client';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';

// Lazy load all other pages for optimal bundle splitting
const NotFound = lazy(() => import('./pages/not-found'));
const ForYou = lazy(() => import('./pages/for-you'));
const Landing = lazy(() => import('./pages/landing'));
const LoginPage = lazy(() => import('./pages/login'));
const Leagues = lazy(() => import('./pages/leagues'));
const LeagueDetail = lazy(() => import('./pages/league-detail'));
const LeagueMembers = lazy(() => import('./pages/league-members'));
const CreateLeagueWizard = lazy(() => import('./pages/create-league-wizard'));
const CreateLeagueDirect = lazy(() => import('./pages/create-league-direct'));
const CreateLeagueSimple = lazy(() => import('./pages/create-league-simple'));
const CreateLeagueNew = lazy(() => import('./pages/create-new'));
const CreateSeason = lazy(() => import('./pages/create-season'));
const Leaderboards = lazy(() => import('./pages/leaderboards'));
const HitsPage = lazy(() => import('./pages/hits').then(m => ({ default: m.HitsPage })));
const HowItWorks = lazy(() => import('./pages/how-it-works'));
const DemoLanding = lazy(() => import('./pages/demo-landing'));
const Predictions = lazy(() => import('./pages/predictions'));
const PredictionsPickem = lazy(() => import('./pages/predictions-pickem'));
const MyBets = lazy(() => import('./pages/my-bets'));
const DailyPage = lazy(() => import('./pages/daily'));
const DailyArchive = lazy(() => import('./pages/daily-archive'));
const DailyRoundDetail = lazy(() => import('./pages/daily-round-detail'));
const DailyInvitePage = lazy(() => import('./pages/daily-invite'));

const Settings = lazy(() => import('./pages/settings'));
const Profile = lazy(() => import('./pages/profile'));
const Search = lazy(() => import('./pages/search'));

const InvitePage = lazy(() => import('./pages/invite'));
const OnboardingPage = lazy(() => import('./pages/onboarding'));
const Discover = lazy(() => import('./pages/discover'));
const Notifications = lazy(() => import('./pages/notifications'));
const AdminDashboard = lazy(() => import('./pages/admin-dashboard'));
const ApiDocumentation = lazy(() => import('./pages/api-documentation'));
const AdminApiExplorer = lazy(() => import('./pages/admin-api-explorer'));
const AdminSpotifySettings = lazy(() => import('./pages/admin-spotify-settings'));
const AdminYouTubeSettings = lazy(() => import('./pages/admin-youtube-settings'));
const AdminTidalSettings = lazy(() => import('./pages/admin-tidal-settings'));
const AdminAppleMusicSettings = lazy(() => import('./pages/admin-apple-music-settings'));
const AppleMusicPlayerTest = lazy(() => import('./pages/apple-music-player-test'));
const AdminMusicFetch = lazy(() => import('./pages/admin-musicfetch'));
const AdminComponentTests = lazy(() => import('./pages/admin-component-tests'));
const AdminEmailTemplateEditor = lazy(() => import('./pages/admin-email-template-editor'));
const AdminNotifications = lazy(() => import('./pages/admin-notifications'));
const AdminPushNotificationHistory = lazy(() => import('./pages/admin-push-notification-history'));
const AppleMusicTokenGenerator = lazy(() => import('./pages/apple-music-token-generator'));
const AppleMusicPlaylist = lazy(() => import('./pages/apple-music-playlist'));
const AppleMusicCapacitorTest = lazy(() => import('./pages/apple-music-capacitor-test'));
const AppleMusicCallback = lazy(() => import('./pages/apple-music-callback'));
const SpotifyAuth = lazy(() => import('./pages/spotify-auth'));
const SpotifyCallback = lazy(() => import('./pages/spotify-callback'));
const YouTubeTestPage = lazy(() => import('./pages/youtube-test'));
const YouTubePlayerTestPage = lazy(() => import('./pages/youtube-player-test'));
const YouTubeCallbackPage = lazy(() => import('./pages/youtube-callback'));
const AdminYouTubeCallbackPage = lazy(() => import('./pages/admin-youtube-callback'));
const TidalCallbackPage = lazy(() => import('./pages/tidal-callback'));
const DiscordLinkCallbackPage = lazy(() => import('./pages/discord-link-callback'));
const DiscordBotCallbackPage = lazy(() => import('./pages/discord-bot-callback'));
const RadioPage = lazy(() => import('./pages/radio'));
const PrivacyPolicy = lazy(() => import('./pages/privacy-policy'));
const TermsOfService = lazy(() => import('./pages/terms-of-service'));
const SupportPage = lazy(() => import('./pages/support'));
const DeleteAccount = lazy(() => import('./pages/delete-account'));
const ForgotPasswordPage = lazy(() => import('./pages/forgot-password'));
const ResetPasswordPage = lazy(() => import('./pages/reset-password'));
const CustomForgotPasswordPage = lazy(() => import('./pages/custom-forgot-password'));
const CustomResetPasswordPage = lazy(() => import('./pages/custom-reset-password'));

const TokenGenerator = lazy(() => import('./pages/token-generator'));
const DirectDbTool = lazy(() => import('./pages/direct-db-tool'));
const DebugTestPage = lazy(() => import('./pages/debug-test'));
const AppleMusicDebugPage = lazy(() => import('./pages/apple-music-debug'));
const JoinSuccessPage = lazy(() => import('./pages/join-success'));
const PublicResultsPage = lazy(() => import('./pages/public-results'));
const Scene = lazy(() => import('./pages/scene'));
const SceneChannel = lazy(() => import('./pages/scene-channel'));
const LiveRoomPage = lazy(() => import('./pages/live-room'));
const LiveTvPage = lazy(() => import('./pages/live-tv'));
const LiveHostPage = lazy(() => import('./pages/live-host'));
const SceneNearby = lazy(() => import('./pages/scene-nearby'));
const SceneMap = lazy(() => import('./pages/scene-map'));
const SceneBrowse = lazy(() => import('./pages/scene-browse'));
const CommunityRedirect = lazy(() => import('./pages/community'));
const CommunityChannelRedirect = lazy(() => import('./pages/community-channel'));
const ZapPage = lazy(() => import('./pages/zap'));
const CratePage = lazy(() => import('./pages/crate'));
const ZapCardPage = lazy(() => import('./pages/zap-card'));
const AnnouncementOverlay = lazy(() =>
  import('./components/AnnouncementModal').then((module) => ({ default: module.AnnouncementOverlay }))
);
const PushNotificationRepromptModal = lazy(() =>
  import('./components/push-notification-reprompt-modal').then((module) => ({
    default: module.PushNotificationRepromptModal,
  }))
);
const ShakeFeedback = lazy(() =>
  import('./components/ShakeFeedback').then((module) => ({ default: module.ShakeFeedback }))
);
const MiniMusicPlayer = lazy(() =>
  import('./components/music/mini-music-player').then((module) => ({ default: module.MiniMusicPlayer }))
);
import PWAWrapper from './components/pwa/pwa-wrapper';
import { MusicPlayerProvider } from './contexts/music-player-context';
import { RadioPlaybackProvider } from './contexts/radio-playback-context';
import { ChatContextProvider } from './contexts/chat-context';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
// Temporarily disabled for production testing
// import { IOSDebugOverlay, iosDebugLog } from './components/debug/ios-debug-overlay';

const transitionAroundNav = (
  navigate: (to: string, options?: { replace?: boolean; state?: unknown }) => void,
  to: string,
  options?: { replace?: boolean; state?: unknown }
) => {
  startTransition(() => {
    navigate(to, options);
  });
};

function Router() {
  const { isAuthenticated, isLoading, user } = useAuth();

  // Debug logging for iOS
  // React.useEffect(() => {
  //   iosDebugLog(`Auth state - isLoading: ${isLoading}, isAuthenticated: ${isAuthenticated}`);
  // }, [isLoading, isAuthenticated]);

  // Show loading while checking authentication
  if (isLoading) {
    // iosDebugLog('Showing loading spinner - authentication in progress');
    return (
      <div className="flex items-center justify-center min-h-screen">
        <ThemeMascot size="lg" />
      </div>
    );
  }

  // Show "Creating Account" screen when user is authenticated but needs onboarding
  // This prevents showing "Welcome back" flash before redirect to onboarding
  const isOnOnboardingPage = window.location.pathname === '/onboarding';
  const isOnInvitePage = window.location.pathname.startsWith('/invite/');
  const isOnLoginPage = window.location.pathname === '/login';

  // User needs onboarding if they have a temporary username
  const needsOnboarding = user?.username?.startsWith('user_');

  // Check if a new signup just happened - we use sessionStorage to track this
  // This helps show loading screen during the gap between Firebase auth and API user data loading
  const justSignedUp = sessionStorage.getItem('yapzap_just_signed_up');

  // Show loading screen during new signup flow when:
  // 1. A signup just happened AND we're authenticated but user data hasn't loaded yet, OR
  // 2. User is authenticated AND has temporary username (needs onboarding)
  const showNewUserLoading = isAuthenticated && (
    (justSignedUp && !user) ||  // Signup happened, waiting for user data
    needsOnboarding              // User data loaded, needs onboarding
  );

  // Clear the signup flag once user data is loaded (either needs onboarding or not)
  if (justSignedUp && user) {
    sessionStorage.removeItem('yapzap_just_signed_up');
  }

  if (!isOnOnboardingPage && !isOnInvitePage && !isOnLoginPage && showNewUserLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-purple-800">
        <div className="text-center space-y-4">
          <ThemeMascot size="lg" className="mx-auto" />
          <h2 className="text-xl font-semibold text-white">Setting up your account...</h2>
          <p className="text-purple-200 text-sm">Just a moment while we prepare everything</p>
        </div>
      </div>
    );
  }

  // Dynamic home route component that checks auth status on every render
  const HomeRoute = () => {
    // CRITICAL: Check signed-out flag directly here to prevent stale closures
    const signedOutFlag = localStorage.getItem('yapzap_signed_out');
    const shouldShowLanding = signedOutFlag || !isAuthenticated;

    return shouldShowLanding ? <Landing /> : <ForYou />;
  };

  return (
    <div>
      <Suspense fallback={
        <div className="flex items-center justify-center min-h-screen">
          <ThemeMascot size="lg" />
        </div>
      }>
        <Switch>
        {/* Show landing page for non-authenticated users, for-you for authenticated */}
        <Route path="/" component={HomeRoute} />
        {/* Explicit For You route - requires authentication with sign-in prompt */}
        <ProtectedRoute
          path="/for-you"
          component={ForYou}
          promptTitle="Sign in to see your personalized feed"
          promptDescription="Get personalized content, track your leagues, and see upcoming deadlines."
        />
        
        {/* Redirect old home route to for-you for backward compatibility */}
        <Route
          path="/home"
          component={() => {
            window.location.href = "/";
            return null;
          }}
        />
        <Route path="/login" component={LoginPage} />
        <Route path="/privacy-policy" component={PrivacyPolicy} />
        <Route path="/terms-of-service" component={TermsOfService} />
        <Route path="/support" component={SupportPage} />
        <Route path="/delete-account" component={DeleteAccount} />
        <Route path="/radio/:roundId?" component={RadioPage} />
        <Route path="/discover" component={Discover} />
        <Route path="/hits" component={HitsPage} />
        <Route path="/how-it-works" component={HowItWorks} />
        <Route path="/demo" component={DemoLanding} />
        <Route path="/daily" component={DailyPage} />
        <Route path="/daily/archive" component={DailyArchive} />
        <Route path="/daily/invite/:code" component={DailyInvitePage} />
        <Route path="/daily/:roundId" component={DailyRoundDetail} />
        <Route path="/predictions/pick-em" component={PredictionsPickem} />
        <Route path="/predictions/my-bets" component={MyBets} />
        <Route path="/predictions" component={Predictions} />
        <Route path="/leaderboards" component={Leaderboards} />
        <Route path="/scene/nearby" component={SceneNearby} />
        <Route path="/scene/map" component={SceneMap} />
        <Route path="/scene/browse" component={SceneBrowse} />
        <Route path="/scene/:id" component={SceneChannel} />
        <Route path="/scene" component={Scene} />
        {/* YapZap Live jukebox: public guest page, chrome-less TV page, owner remote */}
        <Route path="/live/:slug/tv" component={LiveTvPage} />
        <ProtectedRoute path="/live/:slug/host" component={LiveHostPage} />
        <Route path="/live/:slug" component={LiveRoomPage} />
        {/* Song Zapping routes */}
        <Route path="/zap" component={ZapPage} />
        <Route path="/crate" component={CratePage} />
        <Route path="/zap/history">{() => { useEffect(() => { window.location.replace("/crate?tab=history"); }, []); return null; }}</Route>
        <Route path="/zap/card/:trackId/:shareToken" component={ZapCardPage} />
        <Route path="/zap/card/:captureId" component={ZapCardPage} /> {/* Legacy redirect */}
        {/* Backwards compatibility redirects */}
        <Route path="/community/:id" component={CommunityChannelRedirect} />
        <Route path="/community" component={CommunityRedirect} />

        {/* Public shareable results page (no auth required) */}
        <Route path="/results/:roundId" component={PublicResultsPage} />

        {/* League detail routes - Must come before general /leagues route */}
        <Route path="/leagues/:id/members">
          <LeagueMembers />
        </Route>
        <ProtectedRoute path="/leagues/:id/create-season" component={CreateSeason} />
        <Route path="/leagues/:id" component={LeagueDetail} />

        {/* Protected Routes - Require Authentication */}
        <ProtectedRoute
          path="/leagues"
          component={Leagues}
          promptTitle="Sign in to see your leagues"
          promptDescription="View and manage your music and video competition leagues."
        />

        {/* Public invite page for private leagues */}
        <Route path="/invite/:inviteCode" component={InvitePage} />
        {/* Redirect old /join/ routes to /invite/ for consistency */}
        <Route
          path="/join/:inviteCode"
          component={() => {
            const code = window.location.pathname.split('/join/')[1];
            window.location.href = `/invite/${code}`;
            return null;
          }}
        />
        <ProtectedRoute path="/create" component={CreateLeagueWizard} />
        <ProtectedRoute path="/create-direct" component={CreateLeagueDirect} />
        <ProtectedRoute path="/create-simple" component={CreateLeagueSimple} />
        <ProtectedRoute path="/create-new" component={CreateLeagueNew} />
        {/* Profile route - Allow public access to other user profiles */}
        <ProtectedRoute path="/profile" component={Profile} />
        <Route path="/profile/:username" component={Profile} />
        <ProtectedRoute path="/notifications" component={Notifications} />
        <ProtectedRoute
          path="/settings"
          component={Settings}
          promptTitle="Sign in to access settings"
          promptDescription="Manage your profile, music services, notifications, and account preferences."
        />
        <ProtectedRoute path="/search" component={Search} />

        {/* Admin Routes - Require Authentication */}
        {/* Specific admin routes that need special components */}
        <ProtectedRoute
          path="/admin/api-documentation"
          component={ApiDocumentation}
        />
        <ProtectedRoute
          path="/admin/api-explorer"
          component={AdminApiExplorer}
        />
        <ProtectedRoute
          path="/admin/spotify-settings"
          component={AdminSpotifySettings}
        />
        <ProtectedRoute
          path="/admin/youtube-settings"
          component={AdminYouTubeSettings}
        />
        <ProtectedRoute
          path="/admin/tidal-settings"
          component={AdminTidalSettings}
        />
        <ProtectedRoute
          path="/admin/apple-music-settings"
          component={AdminAppleMusicSettings}
        />
        <ProtectedRoute
          path="/admin/apple-music-player-test"
          component={AppleMusicPlayerTest}
        />
        <ProtectedRoute
          path="/admin/apple-music-token"
          component={AppleMusicTokenGenerator}
        />
        <ProtectedRoute path="/admin/musicfetch" component={AdminMusicFetch} />
        <ProtectedRoute
          path="/admin/component-tests"
          component={AdminComponentTests}
        />
        <ProtectedRoute
          path="/admin/email-template-editor"
          component={AdminEmailTemplateEditor}
        />
        <ProtectedRoute
          path="/admin/notifications"
          component={AdminNotifications}
        />
        <ProtectedRoute
          path="/admin/push-notifications/history"
          component={AdminPushNotificationHistory}
        />

        {/* Catch-all admin route - handles tab-based admin pages like /admin/global-images */}
        <ProtectedRoute path="/admin/:tab?" component={AdminDashboard} />
        <ProtectedRoute path="/admin-dashboard" component={AdminDashboard} />

        {/* Spotify OAuth Routes - Public access for authorization flow */}
        <Route path="/spotify-auth" component={SpotifyAuth} />
        <Route path="/spotify-callback" component={SpotifyCallback} />

        {/* Apple Music Playlist Page - Public access for playlist viewing */}
        <Route path="/apple-music-playlist" component={AppleMusicPlaylist} />

        {/* YouTube Integration Pages */}
        <Route path="/youtube-test" component={YouTubeTestPage} />
        <Route path="/youtube-player-test" component={YouTubePlayerTestPage} />
        <Route path="/youtube-callback" component={YouTubeCallbackPage} />
        <Route path="/auth/youtube/callback" component={YouTubeCallbackPage} />
        {/* Handle YouTube OAuth redirects that might go to youtube-test */}
        <Route path="/youtube-test/callback" component={YouTubeCallbackPage} />
        {/* Admin YouTube OAuth callback - public route for Google OAuth redirect */}
        <Route path="/api/admin/youtube/admin-callback" component={AdminYouTubeCallbackPage} />

        {/* TIDAL Integration */}
        <Route path="/auth/tidal/callback" component={TidalCallbackPage} />
        <Route path="/tidal-callback" component={TidalCallbackPage} />
        <Route path="/discord-link-callback" component={DiscordLinkCallbackPage} />
        <Route path="/discord-bot-callback" component={DiscordBotCallbackPage} />

        {/* Firebase Authentication - New authentication system */}
        <Route path="/forgot-password" component={CustomForgotPasswordPage} />
        <Route path="/reset-password" component={CustomResetPasswordPage} />

        {/* Onboarding for new users */}
        <ProtectedRoute path="/onboarding" component={OnboardingPage} />

        <ProtectedRoute path="/token-generator" component={TokenGenerator} />

        {/* Invite Routes - Public access for league invitations */}
        <Route path="/invite/:inviteCode" component={InvitePage} />
        <Route path="/invite" component={InvitePage} />

        {/* Join Success - Celebration screen after joining a league */}
        <Route path="/join-success" component={JoinSuccessPage} />

        {/* Direct Database Tool - Allow public access for testing */}
        <Route path="/db-tool" component={DirectDbTool} />

        {/* Debug Test Page - For testing the debug system */}
        <Route path="/debug-test" component={DebugTestPage} />


        {/* Apple Music Debug Page - For testing Apple Music issues */}
        <Route path="/apple-music-debug" component={AppleMusicDebugPage} />

        {/* Apple Music Capacitor Test - For testing MusicKit JS in Capacitor */}
        <Route path="/apple-music-capacitor-test" component={AppleMusicCapacitorTest} />

        {/* Apple Music OAuth Callback - Handles OAuth redirect from Apple Music */}
        <Route path="/apple-music-callback" component={AppleMusicCallback} />

        <Route component={NotFound} />
      </Switch>
      </Suspense>
    </div>
  );
}

function DeferredGlobalUi({ ready }: { ready: boolean }) {
  const { isAuthenticated } = useAuth();

  if (!ready) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      {isAuthenticated && <AnnouncementOverlay />}
      <ShakeFeedback />
      <MiniMusicPlayer />
      <PushNotificationRepromptModal />
    </Suspense>
  );
}

function App() {
  const [showDeferredUi, setShowDeferredUi] = useState(false);

  useEffect(() => {
    // Capture ?ref= referrer from URL for referral tracking
    captureReferrer();

    // Initialize debug system
    setupGlobalErrorCatching();
    setupDebugCommands();
    monitorNetworkRequests();

    // Initialize API client debugging
    debugApiClient.addInterceptor({
      onError: (error, config) => {
        console.error(`API Error: ${config.method} ${config.url}`, error);
      },
    });

    // Handle Apple Music deep link callback (Capacitor only)
    if (Capacitor.isNativePlatform()) {
      const handleDeepLink = CapApp.addListener('appUrlOpen', async (data: any) => {
        console.log('🍎 Deep link received:', data.url);

        // Handle both Universal Links (https://) and custom URL scheme (yapzap://)
        const isAppleMusicCallback = data.url.includes('apple-music-callback') &&
                                     (data.url.startsWith('yapzap://') ||
                                      data.url.startsWith('https://yapzap.com/') ||
                                      data.url.startsWith('https://www.yapzap.com/'));

        if (isAppleMusicCallback) {
          try {
            console.log('🍎 Handling Apple Music OAuth callback...');
            const url = new URL(data.url);

            // Try to get authorization code first (from Apple Music OAuth)
            let authCode = url.searchParams.get('code');
            let stateParam = url.searchParams.get('state');

            // If we have an authorization code, exchange it for a user token
            if (authCode) {
              console.log('🍎 Exchanging authorization code for user token...');
              const { apiRequest: apiReq } = await import('./lib/queryClient');

              const exchangeResponse = await apiReq('/api/music/apple-music/exchange-code', 'POST', {
                code: authCode,
                state: stateParam
              });

              if (!exchangeResponse?.success) {
                throw new Error(exchangeResponse?.error || 'Failed to exchange authorization code');
              }

              console.log('🍎 Successfully exchanged code for user token');
              // Token is already saved by the exchange endpoint

              // Clean up sessionStorage
              sessionStorage.removeItem('apple_music_auth_state');
              sessionStorage.removeItem('apple_music_dev_token');

              return;
            }

            // Fallback: Try query parameter token (from legacy custom URL scheme flow)
            let musicUserToken = url.searchParams.get('token');

            // Fallback: fragment-based token (legacy flow)
            if (!musicUserToken) {
              const fragment = url.hash.substring(1); // Remove the #
              const params = new URLSearchParams(fragment);
              musicUserToken = params.get('music_user_token');
            }

            if (!musicUserToken) {
              console.error('🍎 No authorization code or music user token found in callback URL');
              console.error('🍎 URL:', data.url);
              return;
            }

            console.log('🍎 Received Apple Music token from legacy callback');

            // CRITICAL: Clear localStorage tokens first
            console.log('🧹 Clearing existing Apple Music tokens from localStorage...');
            for (let i = localStorage.length - 1; i >= 0; i--) {
              const key = localStorage.key(i);
              if (key?.startsWith('music.') && key.endsWith('.u')) {
                console.log(`🍎 Removing cached token: ${key}`);
                localStorage.removeItem(key);
              }
            }

            // Save the user token to the backend
            const { apiRequest: apiReq } = await import('./lib/queryClient');
            await apiReq('POST', '/api/music/apple-music/save-token', {
              userToken: musicUserToken
            });

            console.log('🍎 Successfully saved Apple Music token to backend');

            // Clean up sessionStorage
            sessionStorage.removeItem('apple_music_dev_token');

            // Show success notification (if toast is available in this context)
            // The settings page will update automatically when it refetches auth status

          } catch (error) {
            console.error('🍎 Error handling Apple Music callback:', error);
          }
        }
      });

      return () => {
        handleDeepLink.remove();
      };
    }
  }, []);

  useEffect(() => {
    const browserWindow = window as Window & {
      requestIdleCallback?: (callback: () => void, options?: { timeout?: number }) => number;
      cancelIdleCallback?: (handle: number) => void;
    };

    if (browserWindow.requestIdleCallback) {
      const idleHandle = browserWindow.requestIdleCallback(() => {
        setShowDeferredUi(true);
      }, { timeout: 1500 });

      return () => {
        browserWindow.cancelIdleCallback?.(idleHandle);
      };
    }

    const timeoutId = window.setTimeout(() => {
      setShowDeferredUi(true);
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="light" storageKey="vidleague-theme">
          <WouterRouter aroundNav={transitionAroundNav}>
            <FirebaseAuthProvider>
              <AuthProvider>
                <ChatContextProvider>
                <UserProvider>
                  <RealTimeUpdatesProvider>
                    <ErrorProvider>
                      <AnalyticsProvider>
                        <TooltipProvider>
                          <MusicPlayerProvider>
                            <RadioPlaybackProvider>
                            <TourProvider>
                            <PWAWrapper>
                              <Toaster />
                              <Router />
                              <DeferredGlobalUi ready={showDeferredUi} />
                            </PWAWrapper>
                            </TourProvider>
                            </RadioPlaybackProvider>
                          </MusicPlayerProvider>
                        </TooltipProvider>
                      </AnalyticsProvider>
                    </ErrorProvider>
                  </RealTimeUpdatesProvider>
                </UserProvider>
                </ChatContextProvider>
              </AuthProvider>
            </FirebaseAuthProvider>
          </WouterRouter>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
